import React from 'react';
import PropTypes from 'prop-types';
import ErrorRecovery from './ErrorRecovery';
import '../../styles/common.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      eventId: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    // Log error details
    const errorDetails = {
      error: error.toString(),
      errorInfo: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    // Store error details for debugging
    localStorage.setItem('lastError', JSON.stringify(errorDetails));
    
    // Update state with error information
    this.setState({
      error,
      errorInfo,
      eventId: Date.now().toString()
    });

    // Report to error monitoring service (if available)
    if (window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        }
      });
    }
  }

  handleReload = () => {
    // Clear error state and reload
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    });
    
    // Force a page reload as fallback
    window.location.reload();
  };

  handleRetry = () => {
    // Just clear the error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      const { fallback: CustomFallback } = this.props;
      
      if (CustomFallback) {
        return (
          <CustomFallback 
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onRetry={this.handleRetry}
            onReload={this.handleReload}
          />
        );
      }

      // Use enhanced error recovery component
      return (
        <ErrorRecovery
          error={this.state.error}
          onRetry={this.handleRetry}
          onReportError={() => this.reportErrorToService(this.state.error, this.state.errorInfo)}
          errorBoundary={true}
          maxRetries={3}
          retryDelay={2000}
          fallbackComponent={
            <div className="error-boundary-container">
              <div className="error-boundary-content">
                <div className="error-icon">💥</div>
                <h2>Critical Error</h2>
                <p>The application has encountered a critical error and cannot recover.</p>
                <button className="primary-button" onClick={this.handleReload}>
                  Reload Application
                </button>
                <p className="error-id">Error ID: {this.state.eventId}</p>
              </div>
            </div>
          }
        />
      );
    }

    return this.props.children;
  }

  reportErrorToService = async (error, errorInfo) => {
    try {
      const errorReport = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: sessionStorage.getItem('canvasUserId') || 'anonymous',
        eventId: this.state.eventId
      };
      
      console.log('Reporting error:', errorReport);
      // In production, send to error monitoring service
      // await errorService.report(errorReport);
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
    }
  };
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.elementType
};

export default ErrorBoundary;
